/**
 * Adapted from PlayCanvas Engine's gsplat-container.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type {
  GSplatGraphicsAdapter,
  GSplatMaterialLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import {
  GSplatResourceBase,
  type GSplatResourceDataSource,
  type GSplatWorkBufferModifierSource,
} from '@/plugins/gsplat/unified/resource/GSplatResourceBase'

type GSplatContainerData = GSplatResourceDataSource

function validateCapacity(
  adapter: GSplatGraphicsAdapter,
  format: GSplatFormat,
  maxSplats: number
): void {
  if (!Number.isSafeInteger(maxSplats) || maxSplats < 0) {
    throw new Error(`非法 GSplatContainer capacity: ${maxSplats}`)
  }
  if (format.graphicsAdapter !== adapter) {
    throw new Error('GSplatContainer format 与 adapter 不属于同一 graphics device')
  }
  const dimensions = adapter.calculateTextureDimensions(maxSplats)
  const capacity = dimensions.width * dimensions.height
  if (
    !Number.isSafeInteger(dimensions.width) ||
    !Number.isSafeInteger(dimensions.height) ||
    dimensions.width < 1 ||
    dimensions.height < 1 ||
    dimensions.width > adapter.limits.maxTextureDimension2D ||
    dimensions.height > adapter.limits.maxTextureDimension2D ||
    !Number.isSafeInteger(capacity) ||
    capacity < maxSplats
  ) {
    throw new Error(
      `GSplatContainer capacity 超出 graphics limit: count=${maxSplats}, ` +
        `size=${dimensions.width}x${dimensions.height}`
    )
  }
}

function createContainerData(maxSplats: number): GSplatContainerData {
  const aabb = new THREE.Box3()
  return {
    numSplats: maxSplats,
    getCenters: () => null,
    calcAabb: (target) => {
      target.copy(aabb)
    },
  }
}

/** 由应用或生成器填充 texture streams 的 procedural Unified Resource。 */
export class GSplatContainer extends GSplatResourceBase<GSplatContainerData> {
  readonly kind = 'container' as const
  private readonly maxSplatsValue: number
  private numSplatsValue: number

  constructor(adapter: GSplatGraphicsAdapter, maxSplats: number, format: GSplatFormat) {
    validateCapacity(adapter, format, maxSplats)
    super({
      adapter,
      data: createContainerData(maxSplats),
      format,
      prepareCenters: false,
      getCpuExportData: () => null,
    })
    this.maxSplatsValue = maxSplats
    this.numSplatsValue = maxSplats

    const streams = new GSplatStreams(adapter)
    try {
      streams.init(format, maxSplats)
      this.attachStreams(streams)
    } catch (error) {
      streams.destroy()
      this.destroy()
      throw error
    }
  }

  override set centers(value: Float32Array | null) {
    this.assertActive()
    if (value === null || value.length !== this.maxSplatsValue * 3) {
      throw new Error(
        `GSplatContainer centers 长度不匹配：actual=${value?.length ?? 'null'}, ` +
          `expected=${this.maxSplatsValue * 3}`
      )
    }
    super.centers = value
  }

  override get centers(): Float32Array {
    this.assertActive()
    if (!this.hasCenters) {
      super.centers = new Float32Array(this.maxSplatsValue * 3)
      this.centersVersion++
    }
    return super.centers!
  }

  get maxSplats(): number {
    return this.maxSplatsValue
  }

  override get numSplats(): number {
    return this.numSplatsValue
  }

  update(numSplats = this.numSplatsValue, centersUpdated = true): void {
    this.assertActive()
    if (!Number.isSafeInteger(numSplats)) {
      throw new Error(`非法 GSplatContainer splat count: ${numSplats}`)
    }
    this.numSplatsValue = Math.max(0, Math.min(this.maxSplatsValue, numSplats))
    if (centersUpdated) this.centersVersion++
  }

  override configureMaterialDefines(defines: Map<string, unknown>): void {
    super.configureMaterialDefines(defines)
    defines.set('SH_BANDS', 0)
  }

  override configureMaterial(
    material: GSplatMaterialLeaf,
    workBufferModifier: GSplatWorkBufferModifierSource | null,
    formatDeclarations: string
  ): void {
    super.configureMaterial(material, workBufferModifier, formatDeclarations)
    const chunks = material.getShaderChunks(material.language)
    chunks.set(
      'gsplatContainerDeclarationsVS',
      this.format.getInputDeclarations(undefined, material.language)
    )
    chunks.set('gsplatDeclarationsVS', '#include "gsplatContainerDeclVS"')
    chunks.set('gsplatReadVS', this.format.getReadCode(material.language))
  }
}
